<template>
  <div ref="sortableAnswers" class="space-y-4">
    <div v-for="(option, idx) in options" :key="keyField ? idx : option.id">
      <div class="flex space-x-2">
        <ArrowsUpDownIcon v-if="!noRearrange" class="drag-handle w-5 cursor-move" />
        <div v-if="readOnly" class="w-full rounded-sm p-3 text-brand-text ring-1 ring-surface-500/40">
          {{ option.name }}
        </div>
        <template v-else>
          <template v-if="shouldShowInput">
            <slot name="input" :option="option" :index="idx" />
          </template>
          <template v-else>
            <div class="w-full">
              <slot name="originalText" :option="option" :index="idx" />
              <AnswerOptionEditor
                v-if="!isBuiltInOther(option)"
                ref="richTextRefs"
                :option="option"
                class="w-full"
                :disabled="disabled || readOnly" />
              <div
                v-else
                class="w-full rounded-sm bg-primary-50 p-3 text-brand-placeholder ring-1 ring-surface-500/40 dark:bg-slate-700 dark:text-white/70"
                v-html="question?.otherOptions?.otherLabel || 'Other'" />
            </div>
          </template>
        </template>
        <slot name="additionalInput" :option="option" :index="idx" :associated-question="question" />
        <FilterIcon
          v-if="!noFilter"
          class="w-5 cursor-pointer transition-opacity duration-300 ease-in-out hover:opacity-100"
          :class="[doesAnswerHaveConditions(option) ? 'text-sky-400' : 'opacity-50']"
          title="Conditionally show this answer option"
          @click="filterForOption(option)" />
        <ScoreIcon
          v-if="!noScore"
          class="w-5 cursor-pointer transition-opacity duration-300 ease-in-out hover:opacity-100"
          :class="[doesAnswerHaveScore(option) ? 'text-emerald-400' : 'opacity-50']"
          title="Set score for this answer option"
          @click="scoreForOption(option)" />
        <TrashIcon
          v-if="!props.noDelete"
          class="w-5 cursor-pointer opacity-50 transition-opacity duration-300 ease-in-out hover:opacity-100"
          :class="{ invisible: isBuiltInOther(option) }"
          @click="confirmDeletion(option, idx)" />
      </div>
    </div>
  </div>
  <slot name="supplemental" />
  <div
    v-if="!props.noAdd"
    class="mt-6 mb-3 flex cursor-pointer items-center gap-3 rounded-sm bg-primary-50 p-3 text-primary-700 ring-1 ring-primary-500/20 transition-colors duration-300 ease-in-out hover:bg-primary-100 dark:bg-slate-900 dark:text-white/70 dark:hover:bg-slate-800"
    :class="[noRearrange ? 'mr-6' : noMargin ? 'mx-0' : 'mx-6']"
    @click="add">
    <PlusIcon class="w-5" />
    {{ buttonLabel || 'Add another answer option' }}
  </div>

  <Drawer
    v-model:visible="drawerVisible"
    header="Conditions For Answer"
    :modal="true"
    position="right"
    :style="{ width: '40rem' }">
    <div>
      <div class="text-brand-text">
        If a condition is defined below, we'll only show this answer option when the conditions match. If no conditions
        are defined, we'll show this answer option for all users.
      </div>
      <ConditionGroup :condition="condition" :change-type="false" />
    </div>
  </Drawer>

  <Drawer
    v-model:visible="scoreDrawerVisible"
    header="Score For Answer"
    :modal="true"
    position="right"
    :style="{ width: '40rem' }">
    <div>
      <div class="mb-6 text-brand-text dark:text-white/80">
        Set one or more score adjustments when this answer is selected. Each score can increase or decrease
        independently.
      </div>
      <ScoreEditor v-if="currentScoreOption" :option="currentScoreOption" />
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { useSortable } from '@vueuse/integrations/useSortable'
import {
  TrashIcon,
  ArrowsUpDownIcon,
  PlusIcon,
  AdjustmentsHorizontalIcon as FilterIcon,
  ChartBarIcon as ScoreIcon
} from '@heroicons/vue/24/outline'
import type { Segment, Question, QuestionOption } from '@dashboard/types/config'
import {
  doesAnswerHaveConditions,
  doesAnswerHaveScore,
  isBuiltInOther
} from '@dashboard/modules/segment/services/segmentUtilities'
import ScoreEditor from './ScoreEditor.vue'
import AnswerOptionEditor from './AnswerOptionEditor.vue'
import ConditionGroup from '@dashboard/modules/segment/components/ConditionGroup.vue'
import Drawer from 'primevue/drawer'
import type { ConfirmationServiceMethods } from 'primevue/confirmationservice'
import { computed, inject, nextTick, onMounted, ref, useSlots, useTemplateRef, watch } from 'vue'
import type { Ref } from 'vue'

const confirm = inject<ConfirmationServiceMethods>('confirm')

const slots = useSlots()
const richTextRefs = useTemplateRef('richTextRefs')

const shouldShowInput = computed(() => !!slots.input)

const props = defineProps<{
  options: Segment[]
  readOnly?: boolean
  disabled?: boolean
  noRearrange?: boolean
  buttonLabel?: string
  noFilter?: boolean
  noScore?: boolean
  skipConfirmRemove?: boolean
  triggerRearrange?: boolean
  alertLabel?: string
  noMargin?: boolean
  keyField?: string
  noAdd?: boolean
  noDelete?: boolean
}>()

const emit = defineEmits<{
  (e: 'addOption'): void
  (e: 'deleteOption', option: Segment, index: number): void
  (e: 'rearrange', from: number, to: number): void
}>()

const question = inject<Ref<Question | null>>('question', ref(null))

const drawerVisible = ref(false)
const scoreDrawerVisible = ref(false)
const sortableAnswers = ref<HTMLElement | null>(null)
const condition = ref<Record<string, unknown>>({})
const currentScoreOption = ref<QuestionOption | null>(null)
const lastAddedOptionRef = ref(null)

// Watch for changes in option names to handle both list items and line breaks
watch(
  () => props.options,
  (newOptions) => {
    newOptions.forEach((option, index) => {
      if (!option.name) {
        return
      }

      // First check for HTML list items
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = option.name
      const listItems = tempDiv.querySelectorAll('li')

      const firstListItem = listItems[0]
      if (firstListItem) {
        // Handle HTML list items
        option.name = firstListItem.textContent?.trim() || ''
        Array.from(listItems)
          .slice(1)
          .forEach((item) => {
            emit('addOption')
          })

        nextTick(() => {
          Array.from(listItems).forEach((item, arrIdx) => {
            const addedOption = props.options[index + arrIdx]
            if (!addedOption) {
              throw new Error('Missing answer option after adding pasted list items')
            }
            addedOption.name = item.innerHTML
            // const lastIndex = props.options.length - 1
            // props.options[lastIndex].name = item.innerHTML
          })
        })
      } else if (option.name.includes('\n')) {
        // Handle raw text with line breaks
        const lines = option.name
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line)
        const firstLine = lines[0]
        if (lines.length > 1 && firstLine !== undefined) {
          option.name = firstLine
          lines.slice(1).forEach((line) => {
            emit('addOption')
            nextTick(() => {
              const lastIndex = props.options.length - 1
              const addedOption = props.options[lastIndex]
              if (!addedOption) {
                throw new Error('Missing answer option after adding pasted lines')
              }
              addedOption.name = line
            })
          })
        }
      }
    })
  },
  { deep: true }
)

useSortable(sortableAnswers, props.options, {
  handle: '.drag-handle',
  onUpdate: (event) => {
    if (event.oldIndex === undefined || event.newIndex === undefined) {
      return
    }
    if (props.triggerRearrange) {
      emit('rearrange', event.oldIndex, event.newIndex)
    } else {
      const options = props.options
      const [movedOption] = options.splice(event.oldIndex, 1)
      if (movedOption === undefined) {
        return
      }
      options.splice(event.newIndex, 0, movedOption)
    }
  }
})

// Enter key in answer editors: navigate to next field or add a new one
onMounted(() => {
  const container = sortableAnswers.value
  if (!container) {
    return
  }

  container.addEventListener(
    'keydown',
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.shiftKey) {
        return
      }
      if (!(event.target as HTMLElement).closest?.('[contenteditable]')) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      const editors = richTextRefs.value
      if (!editors || editors.length === 0) {
        return
      }

      const currentIdx = editors.findIndex((editorRef) => editorRef?.editor?.view.dom.contains(event.target as Node))
      if (currentIdx === -1) {
        return
      }

      if (currentIdx < editors.length - 1) {
        editors[currentIdx + 1]?.editor?.chain().focus().run()
      } else {
        add()
      }
    },
    true
  )
})

const add = () => {
  emit('addOption')
  nextTick(() => {
    const editors = richTextRefs.value
    if (!editors) {
      return
    }
    const lastIndex = editors.length - 1
    const lastEditor = editors[lastIndex]
    if (lastEditor) {
      lastEditor.editor?.chain().focus().run()
    }
  })
}

const filterForOption = (option: { id: string; name: string }) => {
  const questionOption = question.value?.options?.find((o: QuestionOption) => o.segmentId === option.id)
  const c = questionOption?.conditions || {}
  if (!c['or']) {
    c['or'] = []
  }
  condition.value = c
  drawerVisible.value = true
}

const scoreForOption = (option: { id: string; name: string }) => {
  const questionOption = question.value?.options?.find((o: QuestionOption) => o.segmentId === option.id)
  if (questionOption) {
    currentScoreOption.value = questionOption
    scoreDrawerVisible.value = true
  }
}

const confirmDeletion = (option: Segment, index: number) => {
  if (props.skipConfirmRemove) {
    emit('deleteOption', option, index)
    return
  }
  confirm?.require({
    header: 'Really Delete?',
    message: props.alertLabel || 'Are you sure you want to delete this answer option?',
    acceptProps: { label: 'Delete', severity: 'danger' },
    rejectProps: { label: 'Cancel', outlined: true },
    accept: async () => {
      emit('deleteOption', option, index)
    }
  })
}
</script>
